import { Button, Card, Descriptions, Modal, message, Spin } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { AssetAllocationItem } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import {
  DELETE_ASSET_ALLOCATION_ITEM,
  GET_ASSET_ALLOCATION_ITEM,
} from "../gql/economicIndicators";

interface RouteParams {
  id: string;
}

interface State {
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  assetAllocationItem?: AssetAllocationItem;
}

function ModalWrapper(
  showDialog: boolean,
  deleteAssetAllocationItem: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteAssetAllocationItem}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this asset allocation item?</div>
    </Modal>
  );
}

export default class ViewAssetAllocationItem extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      showDialog: false,
      assetAllocationItem: undefined,
    };
    this.deleteAssetAllocationItem = this.deleteAssetAllocationItem.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAssetAllocationItem();
    });
  }

  getAssetAllocationItem = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ASSET_ALLOCATION_ITEM,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const assetAllocationItem = data.data?.assetAllocationItem;
            this.setState({
              assetAllocationItem: assetAllocationItem,
            });
          } else {
            message.error("Failed to get asset allocation item data");
          }
        });
      },
    );
  };

  deleteAssetAllocationItem(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_ASSET_ALLOCATION_ITEM,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteAssetAllocationItem.result) {
              message.success("Asset allocation item deleted successfuly");
              this.redirectToAllAssetAllocationItemsPage();
            } else {
              message.error("Failed to delete asset allocation item");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllAssetAllocationItemsPage(): void {
    this.props.history.push("/asset-allocation-items");
  }

  render(): JSX.Element | null {
    const { assetAllocationItem, isLoading, id, showDialog } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteAssetAllocationItem,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.edit}
                    >
                      <NavLink to={`/asset-allocation-items/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-asset-allocation-item-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-asset-allocation-item-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Name</b>} key="name">
                  {assetAllocationItem?.name}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
